/**
 * LeadsFilters.tsx — the compact filter + search bar for the Leads list.
 *
 * The panel leads with the DATA, not the form: applied filters show as a row of
 * removable chips, and the full form lives inside a collapsed "Mais filtros"
 * <details> disclosure (default closed) so it never fills the viewport and
 * pushes the table below the fold. Opening the disclosure reveals the same
 * status multi-select, metadata fields, date range and search controls as before.
 *
 * It owns its own form state and, on submit, resolves a ready-to-send
 * `LeadsQuery` — including the client-side hashing of a phone/CPF search term
 * (Q5), which is co-located here so raw PII is consumed at the input and never
 * propagates. Chips are derived from the last APPLIED query (passed in), so
 * removing a chip re-applies a narrowed query directly, without reopening the form.
 *
 * Accessibility: every control has an associated <label>; the status group is a
 * <fieldset>/<legend>; the disclosure is a native <details>/<summary>; each chip
 * remove is a real button with a descriptive aria-label. Apply/Reset are real
 * buttons in a <form> so the panel is fully keyboard-operable (WCAG 2.1.1,
 * 1.3.1, 4.1.2).
 */

import { useCallback, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEAD_STATUSES,
  LEAD_SEARCH_KINDS,
  type LeadSearchKind,
  type LeadStatus,
} from '../../api/types';
import type { LeadsQuery } from '../../api/client';
import { buildSearchTerm } from './searchHash';
import { formatDateOnly } from './formatDateTime';
import { FilterChips, type FilterChip } from '../../components/FilterChips';
import { Field, inputClass } from '../../components/Field';

export interface LeadsFiltersProps {
  /** Called with a resolved, hash-ready query when the operator applies filters. */
  onApply: (query: LeadsQuery) => void;
  /** Whether a fetch is in flight (disables Apply to avoid overlap). */
  busy: boolean;
  /** The last APPLIED query — drives the removable chip row. */
  applied: LeadsQuery;
  /** Active locale for date-chip formatting (pt-BR → dd/mm/yyyy). */
  locale: string;
}

const DEFAULT_LIMIT = 50;

interface FormState {
  statuses: Set<LeadStatus>;
  productCode: string;
  channel: string;
  tenantId: string;
  createdFrom: string;
  createdTo: string;
  statusFrom: string;
  statusTo: string;
  searchKind: LeadSearchKind;
  searchValue: string;
}

const emptyForm = (): FormState => ({
  statuses: new Set<LeadStatus>(),
  productCode: '',
  channel: '',
  tenantId: '',
  createdFrom: '',
  createdTo: '',
  statusFrom: '',
  statusTo: '',
  searchKind: 'phone_hash',
  searchValue: '',
});

// A form draft seeded from an applied query. The search VALUE cannot be reversed
// from its one-way hash, so the search input always re-opens empty (its kind is
// preserved); every other field round-trips.
const queryToForm = (q: LeadsQuery): FormState => ({
  statuses: new Set(q.status ?? []),
  productCode: q.product_code ?? '',
  channel: q.channel ?? '',
  tenantId: q.tenant_id ?? '',
  createdFrom: q.created_from ?? '',
  createdTo: q.created_to ?? '',
  statusFrom: q.status_from ?? '',
  statusTo: q.status_to ?? '',
  searchKind: q.search?.kind ?? 'phone_hash',
  searchValue: '',
});

const blank = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const LeadsFilters = ({ onApply, busy, applied, locale }: LeadsFiltersProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => queryToForm(applied));

  const toggleStatus = useCallback((status: LeadStatus) => {
    setForm((prev) => {
      const statuses = new Set(prev.statuses);
      if (statuses.has(status)) statuses.delete(status);
      else statuses.add(status);
      return { ...prev, statuses };
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // Hash the PII search input BEFORE it leaves this component (Q5).
      const search = await buildSearchTerm(form.searchKind, form.searchValue);
      onApply({
        status: [...form.statuses],
        product_code: blank(form.productCode),
        channel: blank(form.channel),
        tenant_id: blank(form.tenantId),
        created_from: blank(form.createdFrom),
        created_to: blank(form.createdTo),
        status_from: blank(form.statusFrom),
        status_to: blank(form.statusTo),
        search,
        limit: DEFAULT_LIMIT,
      });
    },
    [form, onApply],
  );

  const handleReset = useCallback(() => {
    setForm(emptyForm());
    onApply({ limit: DEFAULT_LIMIT });
  }, [onApply]);

  // Re-apply the applied query minus one field, and keep the draft in sync.
  const applyNarrowed = useCallback(
    (next: LeadsQuery) => {
      onApply(next);
      setForm(queryToForm(next));
    },
    [onApply],
  );

  const removeStatus = useCallback(
    (status: LeadStatus) => {
      applyNarrowed({
        ...applied,
        status: (applied.status ?? []).filter((s) => s !== status),
      });
    },
    [applied, applyNarrowed],
  );

  const SEARCH_HINT_KEYS = {
    phone_hash: 'leads.search.phoneHint',
    cpf_hash: 'leads.search.cpfHint',
  } as const;
  const searchHint = t(
    form.searchKind === 'phone_hash' || form.searchKind === 'cpf_hash'
      ? SEARCH_HINT_KEYS[form.searchKind]
      : 'leads.search.leadIdHint',
  );

  // --- Applied-filter chips (derived from the applied query) ---------------
  const chips: FilterChip[] = [];
  const removeChipLabel = (label: string): string => t('filters.remove', { label });
  (applied.status ?? []).forEach((status) => {
    const label = t(`leadStatus.${status}`);
    chips.push({
      key: `status:${status}`,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => removeStatus(status),
    });
  });
  const textChip = (field: 'product_code' | 'channel' | 'tenant_id', labelKey: string): void => {
    const value = applied[field];
    if (value == null || value === '') return;
    const label = `${t(labelKey)}: ${value}`;
    chips.push({
      key: field,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, [field]: null }),
    });
  };
  textChip('product_code', 'leads.filters.productCode');
  textChip('channel', 'leads.filters.channel');
  textChip('tenant_id', 'leads.filters.tenant');
  const dateChip = (
    field: 'created_from' | 'created_to' | 'status_from' | 'status_to',
    labelKey: string,
  ): void => {
    const value = applied[field];
    if (value == null || value === '') return;
    const label = `${t(labelKey)}: ${formatDateOnly(value, locale)}`;
    chips.push({
      key: field,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, [field]: null }),
    });
  };
  dateChip('created_from', 'leads.filters.createdFrom');
  dateChip('created_to', 'leads.filters.createdTo');
  dateChip('status_from', 'leads.filters.statusFrom');
  dateChip('status_to', 'leads.filters.statusTo');
  if (applied.search != null) {
    // The value is a one-way hash — the chip names the search KIND only, never
    // a (hashed) PII value.
    const label = `${t('leads.search.legend')}: ${t(`leads.search.kind_${applied.search.kind}`)}`;
    chips.push({
      key: 'search',
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, search: null }),
    });
  }

  return (
    <form
      aria-label={t('leads.filters.legend')}
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-xl bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
    >
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-meta uppercase tracking-wide text-ink-3">
            {t('filters.active')}
          </span>
          <FilterChips chips={chips} testid="leads-filter-chips" />
        </div>
      ) : null}

      {/* Search + status stay visible: the two most-used controls (spec slice 4). */}
      {/* Search — hashed client-side (Q5); lead-id passes through. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-meta uppercase tracking-wide text-ink-3">
          {t('leads.search.legend')}
        </legend>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field id="search-kind" label={t('leads.search.kind')}>
            <select
              id="search-kind"
              value={form.searchKind}
              onChange={(e) =>
                setForm((p) => ({ ...p, searchKind: e.target.value as LeadSearchKind }))
              }
              className={inputClass}
            >
              {LEAD_SEARCH_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`leads.search.kind_${kind}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field id="search-value" label={t('leads.search.value')} hint={searchHint}>
            <input
              id="search-value"
              type="text"
              inputMode={form.searchKind === 'phone_hash' ? 'tel' : 'text'}
              value={form.searchValue}
              aria-describedby="search-value-hint"
              onChange={(e) => setForm((p) => ({ ...p, searchValue: e.target.value }))}
              className={`${inputClass} sm:min-w-[18rem]`}
            />
          </Field>
        </div>
      </fieldset>

      {/* Status multi-select — empty = active scope (Q2); tick terminals to widen. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-meta uppercase tracking-wide text-ink-3">
          {t('leads.filters.status')}
        </legend>
        <p className="text-meta text-ink-3">{t('leads.filters.statusHint')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {LEAD_STATUSES.map((status) => (
            <label
              key={status}
              htmlFor={`leads-filter-status-${status}`}
              className="flex items-center gap-1.5 max-md:min-h-6 text-body text-ink-1"
            >
              <input
                type="checkbox"
                id={`leads-filter-status-${status}`}
                name="status"
                value={status}
                checked={form.statuses.has(status)}
                onChange={() => toggleStatus(status)}
                className="h-4 w-4 rounded border-ink-3 bg-surface-1 text-gold focus:ring-outline-focus"
              />
              {t(`leadStatus.${status}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <details className="flex flex-col gap-4" data-testid="leads-filters-disclosure">
        <summary className="cursor-pointer select-none text-body font-medium text-ink-1 marker:text-ink-3">
          {t('filters.more')}
        </summary>

        <div className="mt-3 flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Field id="filter-product" label={t('leads.filters.productCode')}>
              <input
                id="filter-product"
                type="text"
                value={form.productCode}
                onChange={(e) => setForm((p) => ({ ...p, productCode: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-channel" label={t('leads.filters.channel')}>
              <input
                id="filter-channel"
                type="text"
                value={form.channel}
                onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-tenant" label={t('leads.filters.tenant')}>
              <input
                id="filter-tenant"
                type="text"
                value={form.tenantId}
                placeholder={t('leads.filters.tenantAll')}
                onChange={(e) => setForm((p) => ({ ...p, tenantId: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-created-from" label={t('leads.filters.createdFrom')}>
              <input
                id="filter-created-from"
                type="date"
                lang={locale}
                value={form.createdFrom}
                onChange={(e) => setForm((p) => ({ ...p, createdFrom: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-created-to" label={t('leads.filters.createdTo')}>
              <input
                id="filter-created-to"
                type="date"
                lang={locale}
                value={form.createdTo}
                onChange={(e) => setForm((p) => ({ ...p, createdTo: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-status-from" label={t('leads.filters.statusFrom')}>
              <input
                id="filter-status-from"
                type="date"
                lang={locale}
                value={form.statusFrom}
                onChange={(e) => setForm((p) => ({ ...p, statusFrom: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-status-to" label={t('leads.filters.statusTo')}>
              <input
                id="filter-status-to"
                type="date"
                lang={locale}
                value={form.statusTo}
                onChange={(e) => setForm((p) => ({ ...p, statusTo: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
        </div>
      </details>

      <div className="flex gap-3">
        <button
          type="submit"
          data-testid="leads-apply"
          disabled={busy}
          aria-busy={busy}
          className="inline-flex items-center rounded-lg bg-gold min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold-mute disabled:opacity-60"
        >
          {t('leads.filters.apply')}
        </button>
        <button
          type="button"
          data-testid="leads-reset"
          onClick={handleReset}
          disabled={busy}
          className="inline-flex items-center rounded-lg bg-surface-1 min-h-11 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('leads.filters.reset')}
        </button>
      </div>
    </form>
  );
};
